







































































































































































































import Vue from 'vue'
import Component from 'vue-class-component'
import { User, UserData, UserRole } from '@/core/Types'
import { QuerySnapshot } from '@/extern/FirebaseTypes'
import appManager from '@/services/AppManager'
import * as db from '@/core/Database'
import Spinner from '@/components/Spinner.vue'
import { AppColors } from '@/model/AppColors'
import * as _ from 'lodash'
import * as api from '@/services/ApiService'
import { getSubscriptionStatusColor, getSubscriptionStatusText, getUserRoleTitle } from '@/model/Helpers'
import { ManageUserSubscriptionType, Subscription } from '@/core/ApiTypes'
import { SubscriptionStatusTypes } from '@/extern/StripeTypes'
import CreateUserDialog from '@/components/users/CreateUserDialog.vue'
import UserSubscriptionComponent from '@/components/subscriptions/UserSubscriptionComponent.vue'
import SubscriptionPeriodEndComponent from '@/components/subscriptions/SubscriptionPeriodEndComponent.vue'
import EditUserProfileDialog from '@/components/users/EditUserProfileDialog.vue'
import { RoutePaths } from '@/router'

@Component({
    components: {
        Spinner,
        UserSubscriptionComponent,
        SubscriptionPeriodEndComponent,
        CreateUserDialog,
        EditUserProfileDialog
    }
})
export default class UsersView extends Vue {
    isLoadingCompany: boolean = true
    companyId: string|null = null
    stripeCustomerId: string|undefined

    // users
    usersSnapshotCounter: number = 0 // simple way to force a refresh when new items arise
    users: User[] = []
    usersUnsubscriber: VoidFunction|null = null

    // subscriptions
    subscriptionsSnapshotCounter: number = 0 // simple way to force a refresh when new items arise
    subscriptions: Subscription[] = []

    // create user dialog
    isCreateUserDialogVisible: boolean = false
    createUserDialogCounter: number = 0 // simple way to reset the dialog each time it is opened

    // edit user dialog
    isEditUserDialogVisible: boolean = false
    editUser: User|null = null
    editUserDialogCounter: number = 0 // simple way to reset the dialog before opening each time

    // delete user dialog
    isDeleteUserDialogVisible: boolean = false
    deleteUser: User|null = null
    isDeletingUser: boolean = false

    // assign subscription dialog
    isAssignSubscriptionDialogVisible: boolean = false
    isAssigningSubscription: boolean = false
    assignSubscriptionUserId: string|null = null
    assignableSubscriptions: Subscription[] = []
    selectedAssignSubscriptionId: string|null = null

    // unassign subscription dialog
    isUnassignSubscriptionDialogVisible: boolean = false
    isUnassigningSubscription: boolean = false
    unassignSubscriptionUserId: string|null = null
    unassignSubscriptionSubscriptionId: string|null = null

    // noinspection JSUnusedGlobalSymbols
    async created() {
        const user = appManager.userManager.state?.user
        if (user && user.data.companyId) {
            this.usersUnsubscriber = db.streamUsers(
                { companyId: user.data.companyId },
                snapshot => this.onUsersReceived(snapshot),
                error => this.onUsersError(error)
            )

            const company = await db.getCompanyData(user.data.companyId)
            this.companyId = user.data.companyId
            this.stripeCustomerId = company?.stripeCustomerId
            this.isLoadingCompany = false

            await this.loadSubscriptions()
        }
    }

    // noinspection JSUnusedGlobalSymbols
    beforeDestroy() {
        if (this.usersUnsubscriber)
            this.usersUnsubscriber()
    }

    onUsersReceived(snapshot: QuerySnapshot) {
        this.users = snapshot.docs
            .filter(doc => doc.exists)
            .map(doc => {
                return {
                    id: doc.id,
                    data: doc.data() as UserData
                }
            })

        // sort by role, last name, first name
        this.users.sort((u1, u2) => {
            const roleDelta = u1.data.role.localeCompare(u2.data.role)
            if (roleDelta !== 0)
                return roleDelta

            const lastName1 = u1.data.lastNameLower || ''
            const lastName2 = u2.data.lastNameLower || ''
            const lastNameDelta = lastName1.localeCompare(lastName2)
            if (lastNameDelta !== 0)
                return lastNameDelta

            const firstName1 = u1.data.firstNameLower || ''
            const firstName2 = u2.data.firstNameLower || ''
            return firstName1.localeCompare(firstName2)
        })

        this.updateAssignableSubscriptions()
        this.usersSnapshotCounter += 1
    }

    onUsersError(error: Error) {
        console.error(`Error receiving users: ${error}`)
    }

    isLoggedInUser(user: User): boolean {
        return appManager.userManager.state?.user?.id === user.id
    }

    async loadSubscriptions(): Promise<void> {
        if (!this.companyId || !this.stripeCustomerId)
            return

        let hasMore: boolean = true
        while (hasMore) {
            try {
                const response = await appManager.subscriptionManager.querySubscriptions({
                    companyId: this.companyId,
                    stripeCustomerId: this.stripeCustomerId,
                    startAfterStripeSubscriptionId: _.last(this.subscriptions)?.id
                })

                this.subscriptions.push(...response.subscriptions)
                this.subscriptionsSnapshotCounter += 1
                hasMore = response.hasMore
            } catch (error) {
                console.error(`Error Loading Subscriptions: ${error}`)
                hasMore = false
            }
        }

        this.updateAssignableSubscriptions()
    }

    updateAssignableSubscriptions() {
        this.assignableSubscriptions = this.subscriptions.filter(s => this.isSubscriptionAssignable(s))
    }

    getUserSubscriptionKey(user: User): string {
        const subscriptionIds = user?.data?.stripeSubscriptionIds?.join(',') || ''
        return `${user?.id} - ${subscriptionIds} - ${this.usersSnapshotCounter}`
    }

    hasSubscription(user: User): boolean {
        return !!user?.data?.stripeSubscriptionIds && user.data.stripeSubscriptionIds.length > 0
    }

    isSubscriptionAssignable(subscription: Subscription): boolean {
        // subscription is assignable if it's not canceled and it has available licenses remaining
        if (subscription.data.status === SubscriptionStatusTypes.canceled)
            return false

        const userCount = this.getSubscriptionUserCount(subscription)
        return subscription.data.quantity > userCount
    }

    getSubscriptionUserCount(subscription: Subscription): number {
        let count = 0
        for (const user of this.users) {
            if (user.data.stripeSubscriptionIds?.includes(subscription.id))
                ++count
        }
        return count
    }

    getUserIcon(user: User): string {
        if (user.data.role === UserRole.ADMIN)
            return 'mdi-account-key-outline'
        if (user.data.role === UserRole.COMPANY_OWNER)
            return 'mdi-account-star-outline'

        return 'mdi-account-outline'
    }

    getUserIconColor(user: User): string {
        if (user.data.role === UserRole.ADMIN)
            return AppColors.success
        if (user.data.role === UserRole.COMPANY_OWNER)
            return AppColors.secondary

        return AppColors.logoBlue2
    }

    getUserRoleText(user: User): string {
        return getUserRoleTitle(user.data.role)
    }

    getUserName(user: User): string {
        return user.data.name || ''
    }

    getUserEmail(user: User): string {
        return user.data.email || ''
    }

    isUserTester(user: User): boolean {
        return user.data.isTester === true
    }

    getSubscriptionStatusColorLocal(subscription: Subscription): string {
        return getSubscriptionStatusColor(subscription)
    }

    getSubscriptionStatusTextLocal(subscription: Subscription): string {
        return getSubscriptionStatusText(subscription)
    }

    getUserNameById(userId: string|null): string {
        let name = ''
        if (userId) {
            const index = this.users.findIndex(u => u.id === userId)
            if (index >= 0)
                name = this.users[index].data.name
        }
        return name
    }

    get selectedAssignSubscriptionIndex(): number|undefined {
        let index = -1
        if (this.selectedAssignSubscriptionId)
            index = this.assignableSubscriptions.findIndex(s => s.id === this.selectedAssignSubscriptionId)
        return index
    }

    // noinspection JSUnusedGlobalSymbols
    set selectedAssignSubscriptionIndex(index: number|undefined) {
        if (index !== undefined && index >= 0 && index < this.assignableSubscriptions.length)
            this.selectedAssignSubscriptionId = this.assignableSubscriptions[index].id
        else
            this.selectedAssignSubscriptionId = null
    }

    onManageSubscriptionsPressed() {
        this.$router.push(RoutePaths.SUBSCRIPTIONS)
    }

    onAssignSubscriptionPressed(user: User) {
        this.selectedAssignSubscriptionIndex = 0
        this.assignSubscriptionUserId = user.id
        this.isAssignSubscriptionDialogVisible = true
    }

    async onAssignSubscriptionConfirmed() {
        if (this.assignSubscriptionUserId && this.selectedAssignSubscriptionId) {
            this.isAssigningSubscription = true
            try {
                await api.manageUserSubscription({
                    userId: this.assignSubscriptionUserId,
                    subscriptionId: this.selectedAssignSubscriptionId,
                    type: ManageUserSubscriptionType.ASSIGN
                })
            } catch (error) {
                console.error(`Error Assigning Subscription: ${error}`)
            }

            this.isAssigningSubscription = false
            this.isAssignSubscriptionDialogVisible = false
        }
    }

    onUnassignSubscriptionPressed(user: User) {
        const subscriptionId = _.first(user?.data?.stripeSubscriptionIds)
        if (subscriptionId) {
            this.unassignSubscriptionUserId = user.id
            this.unassignSubscriptionSubscriptionId = subscriptionId
            this.isUnassigningSubscription = false
            this.isUnassignSubscriptionDialogVisible = true
        } else {
            this.unassignSubscriptionUserId = null
            this.unassignSubscriptionSubscriptionId = null
        }
    }

    async onUnassignSubscriptionConfirmed() {
        this.isUnassigningSubscription = true

        if (this.unassignSubscriptionUserId && this.unassignSubscriptionSubscriptionId) {
            try {
                await api.manageUserSubscription({
                    userId: this.unassignSubscriptionUserId,
                    subscriptionId: this.unassignSubscriptionSubscriptionId,
                    type: ManageUserSubscriptionType.UNASSIGN
                })
            } catch (error) {
                console.error(`Error Unassigning Subscription: ${error}`)
            }
        }

        this.isUnassigningSubscription = false
        this.isUnassignSubscriptionDialogVisible = false
    }

    onAddUserPressed() {
        this.createUserDialogCounter += 1
        this.isCreateUserDialogVisible = true
    }

    onCloseCreateUserDialogRequested() {
        this.isCreateUserDialogVisible = false
    }

    onEditUserPressed(user: User) {
        this.editUserDialogCounter += 1
        this.editUser = user
        this.isEditUserDialogVisible = true
    }

    onCloseEditUserDialogRequested() {
        this.isEditUserDialogVisible = false
    }

    onDeleteUserPressed(user: User) {
        this.isDeletingUser = false
        this.deleteUser = user
        this.isDeleteUserDialogVisible = true
    }

    async onDeleteUserConfirmed() {
        const deleteUserId = this.deleteUser?.id
        if (deleteUserId) {
            this.isDeletingUser = true

            try {
                await api.deleteUser({userId: deleteUserId})
                appManager.alertManager.publishSuccess('Successfully deleted user')
            } catch (error) {
                console.error(`Error Deleting User: ${error}`)
                appManager.alertManager.publishError('Error deleting user')
            }
        }
        this.isDeletingUser = false
        this.deleteUser = null
        this.isDeleteUserDialogVisible = false
    }
}
